import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  InputAdornment,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  LocalHospital,
  Search,
  Visibility,
  PersonAdd,
  Close,
  LocationOn
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

// Utility function to fix S3 URL format
const fixS3Url = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  // Check if it's an S3 URL with regional endpoint
  const s3RegionalPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
  const match = url.match(s3RegionalPattern);

  if (match) {
    const bucketName = match[1];
    const path = match[3];

    // Convert to path-style URL (works better with CORS)
    const fixedUrl = `https://${bucketName}.s3.amazonaws.com/${path}`;
    console.log('🔧 Fixed S3 URL:', url, '->', fixedUrl);
    return fixedUrl;
  }

  return url;
};
const API_URL = process.env.REACT_APP_API_URL;

interface Doctor {
  id: string;
  name: string;
  email: string;
  phone: string;
  specialization: string;
  licenseNumber: string;
  yearsOfExperience: number;
  status: 'active' | 'inactive' | 'on_leave';
  patientsCount: number;
  joinedDate: string;
  department?: string;
  hospital_name?: string;
  location?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
  profile_image?: string;
  doctor_code?: string;
}

interface PlaceSuggestion {
  description: string;
  place_id: string;
  lat?: number;
  lon?: number;
}

export default function DoctorsPage() {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [filteredDoctors, setFilteredDoctors] = useState<Doctor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const urlStatus = searchParams.get('status');
  const [statusFilter, setStatusFilter] = useState(urlStatus && ['active', 'inactive', 'on_leave', 'all'].includes(urlStatus) ? urlStatus : 'all');
  
  // Sync filter when URL parameter changes
  useEffect(() => {
    const status = searchParams.get('status');
    if (status && ['active', 'inactive', 'on_leave', 'all'].includes(status)) {
      setStatusFilter(status);
    }
  }, [searchParams]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  const [viewDoctor, setViewDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    specialization: '',
    licenseNumber: '',
    yearsOfExperience: '',
    department: '',
    hospital_name: '',
    location: '',
    city: '',
    doctor_code: '',
    latitude: null as number | null,
    longitude: null as number | null
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    specialization: '',
    licenseNumber: '',
    yearsOfExperience: '',
    department: '',
    hospital_name: '',
    location: '',
    city: '',
    doctor_code: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userRole, setUserRole] = useState<string>('');
  const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
  const location = useLocation();
  const dataGridRef = useRef<any>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Location autocomplete states
  const [locationInput, setLocationInput] = useState('');
  const [placeSuggestions, setPlaceSuggestions] = useState<PlaceSuggestion[]>([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);

  useEffect(() => {
    const getUserRole = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setUserRole('');
          return;
        }

        // Decode JWT token to get user role
        const decodedToken = JSON.parse(atob(token.split('.')[1]));
        const role = decodedToken?.role?.toLowerCase() || '';
        setUserRole(role);
      } catch (err) {
        console.error('Error fetching user role:', err);
        setUserRole('');
      }
    };

    getUserRole();
  }, []);

  // Fetch doctors when userRole changes
  useEffect(() => {
    if (userRole) {
      fetchDoctors();
    }
  }, [userRole]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Determine which endpoint to use based on user role
      let endpoint = `${API_URL}/auth/doctors`;
      let headers: any = {
        'accept': 'application/json'
      };
      
      // For marketing users, use the market-user/doctors endpoint
      if (userRole === 'marketinguser') {
        endpoint = `${API_URL}/marketing/market-user/doctors`;
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: headers
      });

      if (response.ok) {
        let mappedDoctors: Doctor[] = [];
        const data = await response.json();
        
        // Handle response structure from market-user endpoint vs auth endpoint
        const doctorsList = userRole === 'marketinguser' ? data.doctors : data;
        
        mappedDoctors = doctorsList.map((doctor: any) => ({
          id: doctor.id,
          name: doctor.full_name || doctor.username || 'N/A',
          email: doctor.email || 'N/A',
          phone: doctor.phoneNumber || '-',
          specialization: doctor.specialization || '-',
          licenseNumber: doctor.license_number || '-',
          yearsOfExperience: doctor.years_of_experience || 0,
          status: doctor.is_active ? 'active' : 'inactive',
          patientsCount: doctor.patients_count || 0,
          joinedDate: doctor.created_at ? new Date(doctor.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          department: doctor.department || '-',
          hospital_name: doctor.hospital_name || '-',
          location: doctor.location || '-',
          city: doctor.city || '-',
          latitude: doctor.latitude,
          longitude: doctor.longitude,
          profile_image: doctor.profile_image || '',
          doctor_code: doctor.doctor_code || ''
        }));

        setDoctors(mappedDoctors);
        setFilteredDoctors(mappedDoctors);
      } else {
        setError('Failed to fetch doctors');
      }
    } catch (err) {
      setError('An error occurred while fetching doctors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = doctors;

    if (searchTerm) {
      filtered = filtered.filter(
        (doctor) =>
          doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doctor.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          doctor.specialization.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((doctor) => doctor.status === statusFilter);
    }

    setFilteredDoctors(filtered);
  }, [searchTerm, statusFilter, doctors]);

  // Handle highlight from navigation state
  useEffect(() => {
    const state = location.state as { highlightId?: string };
    if (state?.highlightId && doctors.length > 0) {
      const targetId = state.highlightId;
      console.log('🎯 Highlighting doctor with ID:', targetId);

      // Find the index of the target doctor
      const targetIndex = doctors.findIndex(d => d.id === targetId);
      console.log('  - Target index in doctors array:', targetIndex);

      if (targetIndex === -1) {
        console.error('  - ❌ Doctor not found in array!');
        return;
      }

      // Set highlighted row
      setHighlightedRowId(targetId);
      console.log('  - highlightedRowId set to:', targetId);

      // Calculate which page the row is on
      const pageSize = paginationModel.pageSize;
      const targetPage = Math.floor(targetIndex / pageSize);
      console.log('  - Target is on page:', targetPage, '(index:', targetIndex, ', pageSize:', pageSize, ')');

      // Change to the correct page
      if (paginationModel.page !== targetPage) {
        console.log('  - Changing from page', paginationModel.page, 'to page', targetPage);
        setPaginationModel({ ...paginationModel, page: targetPage });
      }

      // Scroll to the row after a longer delay to ensure DataGrid is fully rendered
      setTimeout(() => {
        console.log('  - Attempting to scroll to element with data-id:', targetId);
        const element = document.querySelector(`[data-id="${targetId}"]`);
        console.log('  - Element found:', element);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          console.log('  - Scroll initiated');
        } else {
          console.error('  - ❌ Element not found! Trying to find all data-id elements...');
          const allDataIds = document.querySelectorAll('[data-id]');
          console.log('  - All data-id elements:', Array.from(allDataIds).map(el => el.getAttribute('data-id')));
        }
      }, 300); // Reduced delay to 500ms for more responsive scrolling

      // Clear highlight after animation completes
      setTimeout(() => {
        setHighlightedRowId(null);
        // Clear navigation state
        window.history.replaceState({}, document.title);
      }, 10000); // Increased to 10000ms for longer blink visibility
    }
  }, [location.state, doctors]);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 3) return 'Full name must be at least 3 characters';
        if (!/^[a-zA-Z\s.]+$/.test(value)) return 'Full name can only contain letters, spaces, and dots';
        return '';

      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
        return '';

      case 'phoneNumber':
        if (!value.trim()) return 'Phone number is required';
        const cleanPhone = value.replace(/[\s()-]/g, '');
        if (!/^[6-9]\d{9}$/.test(cleanPhone)) return 'Invalid Indian phone number. Must be 10 digits starting with 6-9';
        return '';

      case 'specialization':
        if (!value.trim()) return 'Specialization is required';
        if (value.trim().length < 3) return 'Specialization must be at least 3 characters';
        return '';

      case 'licenseNumber':
        if (!value.trim()) return 'License number is required';
        if (value.trim().length < 5) return 'License number must be at least 5 characters';
        return '';

      case 'yearsOfExperience':
        if (!value) return 'Years of experience is required';
        const years = parseInt(value);
        if (isNaN(years)) return 'Must be a valid number';
        if (years < 0) return 'Cannot be negative';
        if (years > 60) return 'Years of experience cannot exceed 60';
        return '';

      case 'department':
        if (value && value.trim().length < 3) return 'Department must be at least 3 characters';
        return '';

      case 'hospital_name':
        if (value && value.trim().length < 3) return 'Hospital name must be at least 3 characters';
        return '';

      case 'location':
        if (value && value.trim().length < 3) return 'Location must be at least 3 characters';
        return '';

      case 'city':
        if (value && value.trim().length < 2) return 'City must be at least 2 characters';
        return '';

      case 'doctor_code':
        if (value && value.trim().length < 3) return 'Doctor code must be at least 3 characters';
        return '';

      default:
        return '';
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    const error = validateField(field, value);
    setFormErrors({ ...formErrors, [field]: error });
  };

  // Fetch place suggestions from OpenStreetMap Nominatim API (FREE)
  const fetchPlaceSuggestions = async (input: string) => {
    if (!input || input.length < 3) {
      setPlaceSuggestions([]);
      return;
    }

    setLoadingPlaces(true);

    try {
      // Using Nominatim API (OpenStreetMap) - completely free, no API key needed
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(input)}&limit=5&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'CardiReach-HealthMonitoring/1.0' // Required by Nominatim
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const suggestions: PlaceSuggestion[] = data.map((place: any) => ({
          description: place.display_name,
          place_id: place.place_id.toString(),
          lat: parseFloat(place.lat),
          lon: parseFloat(place.lon)
        }));

        setPlaceSuggestions(suggestions);
      } else {
        setPlaceSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching place suggestions:', error);
      setPlaceSuggestions([]);
    } finally {
      setLoadingPlaces(false);
    }
  };

  // Handle location input change
  const handleLocationInputChange = (event: any, value: string) => {
    setLocationInput(value);
    fetchPlaceSuggestions(value);
  };

  // Handle location selection
  const handleLocationSelect = (event: any, value: string | PlaceSuggestion | null) => {
    // If value is a string (user typed without selecting), ignore it
    if (typeof value === 'string') {
      return;
    }

    if (value) {
      setLocationInput(value.description);

      console.log('Selected location:', {
        address: value.description,
        latitude: value.lat,
        longitude: value.lon
      });

      setFormData({
        ...formData,
        location: value.description,
        latitude: value.lat || null,
        longitude: value.lon || null
      });
    } else {
      setLocationInput('');
      setFormData({
        ...formData,
        location: '',
        latitude: null,
        longitude: null
      });
    }
  };

  const validateForm = (): boolean => {
    const errors = {
      name: selectedDoctor ? '' : validateField('name', formData.name),
      email: selectedDoctor ? '' : validateField('email', formData.email),
      phoneNumber: validateField('phoneNumber', formData.phoneNumber),
      specialization: validateField('specialization', formData.specialization),
      licenseNumber: validateField('licenseNumber', formData.licenseNumber),
      yearsOfExperience: validateField('yearsOfExperience', formData.yearsOfExperience),
      department: validateField('department', formData.department),
      hospital_name: validateField('hospital_name', formData.hospital_name),
      location: validateField('location', formData.location),
      city: validateField('city', formData.city),
      doctor_code: validateField('doctor_code', formData.doctor_code)
    };

    setFormErrors(errors);
    return !Object.values(errors).some(error => error !== '');
  };

  const handleOpenDialog = (doctor?: Doctor) => {
    if (doctor) {
      setSelectedDoctor(doctor);
      setFormData({
        name: doctor.name,
        email: doctor.email,
        phoneNumber: doctor.phone,
        specialization: doctor.specialization,
        licenseNumber: doctor.licenseNumber,
        yearsOfExperience: doctor.yearsOfExperience.toString(),
        department: doctor.department || '',
        hospital_name: doctor.hospital_name || '',
        location: doctor.location || '',
        city: doctor.city || '',
        doctor_code: doctor.doctor_code || '',
        latitude: doctor.latitude || null,
        longitude: doctor.longitude || null
      });
      setLocationInput(doctor.location || '');
    } else {
      setSelectedDoctor(null);
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        specialization: '',
        licenseNumber: '',
        yearsOfExperience: '',
        department: '',
        hospital_name: '',
        location: '',
        city: '',
        doctor_code: '',
        latitude: null,
        longitude: null
      });
      setLocationInput('');
    }
    setFormErrors({
      name: '',
      email: '',
      phoneNumber: '',
      specialization: '',
      licenseNumber: '',
      yearsOfExperience: '',
      department: '',
      hospital_name: '',
      location: '',
      city: '',
      doctor_code: ''
    });
    setPlaceSuggestions([]);
    setError('');
    setSuccess('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDoctor(null);
    setError('');
    setSuccess('');
    setSubmitting(false);
  };

  const handleViewDoctor = (doctor: Doctor) => {
    console.log('Viewing doctor:', doctor);
    setViewDoctor(doctor);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewDoctor(null);
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    // Validate form
    if (!validateForm()) {
      setError('Please fix all validation errors before submitting');
      return;
    }

    if (selectedDoctor) {
      // Update doctor - call update API
      try {
        setSubmitting(true);
        const response = await fetch(`${API_URL}/auth/users/${selectedDoctor.id}/doctor-info`, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            phoneNumber: formData.phoneNumber,
            specialization: formData.specialization,
            department: formData.department,
            license_number: formData.licenseNumber,
            years_of_experience: parseInt(formData.yearsOfExperience),
            hospital_name: formData.hospital_name,
            location: formData.location,
            city: formData.city,
            latitude: formData.latitude,
            longitude: formData.longitude
          })
        });

        if (response.ok) {
          setSuccess('Doctor updated successfully!');
          setSubmitting(false);
          // Close dialog first, then refresh
          setTimeout(async () => {
            handleCloseDialog();
            await fetchDoctors();
          }, 1500);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to update doctor');
          setSubmitting(false);
        }
      } catch (err) {
        setError('An error occurred while updating the doctor');
        setSubmitting(false);
      }
    } else {
      // Add new doctor - call register API
      try {
        setSubmitting(true);

        // Get marketing user ID from localStorage
        const userDataString = localStorage.getItem('user');
        let market_executive_id = null;

        if (userDataString) {
          try {
            const userData = JSON.parse(userDataString);
            if (userData && userData.id) {
              market_executive_id = userData.id;
              console.log('Market Executive ID:', market_executive_id);
            }
          } catch (parseError) {
            console.error('Error parsing user data from localStorage:', parseError);
          }
        }

        const response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: formData.name,
            email: formData.email,
            password: '',
            full_name: formData.name,
            phoneNumber: formData.phoneNumber,
            profile_image: '',
            specialization: formData.specialization,
            department: formData.department,
            license_number: formData.licenseNumber,
            years_of_experience: parseInt(formData.yearsOfExperience),
            hospital_name: formData.hospital_name,
            location: formData.location,
            city: formData.city,
            doctor_code: formData.doctor_code,
            latitude: formData.latitude,
            longitude: formData.longitude,
            role: 'doctor',
            market_executive_id: market_executive_id
          })
        });

        if (response.ok) {
          setSuccess('Doctor registered successfully!');
          setSubmitting(false);
          // Close dialog first, then refresh
          setTimeout(async () => {
            handleCloseDialog();
            await fetchDoctors();
          }, 1500);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to register doctor');
          setSubmitting(false);
        }
      } catch (err) {
        setError('An error occurred while registering the doctor');
        setSubmitting(false);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to remove this doctor?')) {
      try {
        const response = await fetch(`${API_URL}/auth/users/${id}`, {
          method: 'DELETE',
          headers: {
            'accept': 'application/json'
          }
        });

        if (response.ok) {
          setSuccess('Doctor deleted successfully!');
          // Refresh the doctors list
          await fetchDoctors();
          setTimeout(() => setSuccess(''), 2000);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to delete doctor');
          setTimeout(() => setError(''), 3000);
        }
      } catch (err) {
        setError('An error occurred while deleting the doctor');
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'on_leave':
        return 'warning';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Doctor Name',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            src={fixS3Url(params.row.profile_image)}
            sx={{
              bgcolor: params.row.profile_image ? 'transparent' : 'primary.main',
              width: 32,
              height: 32,
              border: params.row.profile_image ? '2px solid #e0e0e0' : 'none'
            }}
          >
            {!params.row.profile_image && params.value.charAt(0)}
          </Avatar>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    {
      field: 'specialization',
      headerName: 'Specialization',
      width: 200
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Email fontSize="small" color="action" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    {
      field: 'patientsCount',
      headerName: 'Patients',
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" color="primary" variant="outlined" />
      )
    },
    {
      field: 'yearsOfExperience',
      headerName: 'Experience',
      width: 150,
      renderCell: (params) => `${params.value} years`
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value.replace('_', ' ').toUpperCase()}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton size="small" color="primary" onClick={() => handleViewDoctor(params.row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {/* {(userRole === 'superadmin') && (
            <>
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleOpenDialog(params.row)}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(params.row.id)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )} */}
        </Box>
      )
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* Global success/error messages */}
      {error && !openDialog && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && !openDialog && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
              <TextField
                placeholder="Search doctors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: 1, minWidth: 250, maxWidth: 400 }}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
              />
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {(userRole === 'marketinguser') && (
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={() => handleOpenDialog()}
              >
                Add New Doctor
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Data Grid */}
      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <style>
            {`
              @keyframes blueBlinkAnimation {
                0%, 100% { background-color: transparent; }
                25% { background-color: rgba(33, 150, 243, 0.3); }
                50% { background-color: rgba(33, 150, 243, 0.5); }
                75% { background-color: rgba(33, 150, 243, 0.3); }
              }
              .highlighted-row {
                animation: blueBlinkAnimation 4s ease-in-out;
              }
            `}
          </style>
          <Box sx={{ height: 'calc(100vh - 520px)', minHeight: 400, width: '100%' }}>
            <DataGrid
              ref={dataGridRef}
              rows={filteredDoctors}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              getRowClassName={(params) =>
                params.row.id === highlightedRowId ? 'highlighted-row' : ''
              }
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-columnHeaders': {
                  borderBottom: '2px solid rgba(224, 224, 224, 1)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '2px solid rgba(224, 224, 224, 1)',
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'auto',
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedDoctor ? 'Edit Doctor' : 'Add New Doctor'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2, mt: 2 }}>
              {success}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name || (selectedDoctor ? 'Cannot be changed' : 'This will be used as username and password')}
              required
              disabled={!!selectedDoctor}
              InputProps={{
                readOnly: !!selectedDoctor,
              }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email || (selectedDoctor ? 'Cannot be changed' : 'Enter a valid email address')}
              required
              disabled={!!selectedDoctor}
              InputProps={{
                readOnly: !!selectedDoctor,
              }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
              error={!!formErrors.phoneNumber}
              helperText={formErrors.phoneNumber || 'Enter 10 digit Indian phone number (e.g., 9876543210)'}
              required
              inputProps={{ maxLength: 10 }}
            />
            <TextField
              fullWidth
              label="Specialization"
              value={formData.specialization}
              onChange={(e) => handleFieldChange('specialization', e.target.value)}
              error={!!formErrors.specialization}
              helperText={formErrors.specialization || 'e.g., Cardiologist, General Physician'}
              required
            />
            <TextField
              fullWidth
              label="Department"
              value={formData.department}
              onChange={(e) => handleFieldChange('department', e.target.value)}
              error={!!formErrors.department}
              helperText={formErrors.department || 'Optional: e.g., Cardiology, General Medicine'}
            />
            <TextField
              fullWidth
              label="License Number"
              value={formData.licenseNumber}
              onChange={(e) => handleFieldChange('licenseNumber', e.target.value)}
              error={!!formErrors.licenseNumber}
              helperText={formErrors.licenseNumber || 'Medical license number (min 5 characters)'}
              required
            />
            <TextField
              fullWidth
              label="Years of Experience"
              type="number"
              value={formData.yearsOfExperience}
              onChange={(e) => handleFieldChange('yearsOfExperience', e.target.value)}
              error={!!formErrors.yearsOfExperience}
              helperText={formErrors.yearsOfExperience || 'Enter years of experience (0-60)'}
              required
              inputProps={{ min: 0, max: 60 }}
            />
            <TextField
              fullWidth
              label="Hospital Name"
              value={formData.hospital_name}
              onChange={(e) => handleFieldChange('hospital_name', e.target.value)}
              error={!!formErrors.hospital_name}
              helperText={formErrors.hospital_name || 'Optional: e.g., City General Hospital'}
            />
            <TextField
              fullWidth
              label="City"
              value={formData.city}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              error={!!formErrors.city}
              helperText={formErrors.city || 'Enter city name (used to generate doctor code)'}
            />
            <TextField
              fullWidth
              label="Doctor Code"
              value={formData.doctor_code}
              onChange={(e) => handleFieldChange('doctor_code', e.target.value)}
              error={!!formErrors.doctor_code}
              helperText={formErrors.doctor_code || 'Optional: e.g., CR-MUM-001 (format: CR-CITY_CODE-NUMBER)'}
              disabled={!!selectedDoctor}
              InputProps={{
                readOnly: !!selectedDoctor,
              }}
            />
            <Autocomplete
              freeSolo
              options={placeSuggestions}
              getOptionLabel={(option) => typeof option === 'string' ? option : option.description}
              filterOptions={(x) => x}
              loading={loadingPlaces}
              inputValue={locationInput}
              onInputChange={handleLocationInputChange}
              onChange={handleLocationSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Location"
                  error={!!formErrors.location}
                  helperText={
                    formData.latitude && formData.longitude
                      ? `Selected: ${formData.location} (Lat: ${formData.latitude.toFixed(6)}, Lng: ${formData.longitude.toFixed(6)})`
                      : formErrors.location || 'Start typing to search for a location'
                  }
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LocationOn color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <>
                        {loadingPlaces ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.place_id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOn fontSize="small" color="action" />
                    <Typography variant="body2">{option.description}</Typography>
                  </Box>
                </li>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : selectedDoctor ? 'Update' : 'Register'}
            {!submitting && ' Doctor'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Doctor Dialog */}
      <Dialog open={openViewDialog} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Doctor Details</Typography>
          <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {viewDoctor && (
            <Box>
              {/* Profile Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
                <Avatar
                  src={fixS3Url(viewDoctor.profile_image)}
                  sx={{
                    width: 120,
                    height: 120,
                    mb: 2,
                    bgcolor: viewDoctor.profile_image ? 'transparent' : 'primary.main',
                    fontSize: '3rem',
                    border: '4px solid',
                    borderColor: 'primary.light'
                  }}
                >
                  {(!viewDoctor.profile_image && viewDoctor.name.charAt(0).toUpperCase()) || 'D'}
                </Avatar>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {viewDoctor.name}
                </Typography>
                <Chip
                  label={viewDoctor.status.replace('_', ' ').toUpperCase()}
                  color={getStatusColor(viewDoctor.status) as any}
                  sx={{ mt: 1 }}
                />
              </Box>

              {/* Details Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 3,
                mb: 3
              }}>
                {/* Email */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Email color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Email Address
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.email}</Typography>
                </Box>

                {/* Phone */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Phone color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Phone Number
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.phone || '-'}</Typography>
                </Box>

                {/* Specialization */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocalHospital color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Specialization
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.specialization}</Typography>
                </Box>

                {/* Department */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocalHospital color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Department
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.department}</Typography>
                </Box>

                {/* License Number */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      License Number
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.licenseNumber}</Typography>
                </Box>

                {/* Years of Experience */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Years of Experience
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.yearsOfExperience} years</Typography>
                </Box>

                {/* Hospital Name */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <LocalHospital color="primary" fontSize="small" />
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Hospital Name
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.hospital_name || '-'}</Typography>
                </Box>

                {/* Location */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Location
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.location || '-'}</Typography>
                </Box>

                {/* City */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      City
                    </Typography>
                  </Box>
                  <Typography variant="body1">{viewDoctor.city || '-'}</Typography>
                </Box>

                {/* Doctor Code */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold">
                      Doctor Code
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: 'primary.main' }}>
                    {viewDoctor.doctor_code || '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Statistics Card */}
              <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', mt: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="bold">
                        {viewDoctor.patientsCount}
                      </Typography>
                      <Typography variant="body2">Patients</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="bold">
                        {viewDoctor.yearsOfExperience}
                      </Typography>
                      <Typography variant="body2">Years Experience</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">Joined</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {new Date(viewDoctor.joinedDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog} variant="outlined">
            Close
          </Button>
          {userRole === 'marketinguser' && (
            <Button
              onClick={() => {
                handleCloseViewDialog();
                if (viewDoctor) handleOpenDialog(viewDoctor);
              }}
              variant="contained"
              startIcon={<Edit />}
            >
              Edit Doctor
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
